import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
} from "@material-ui/core";
import RoomContext from "../../context/RoomContext";

const useStyles = makeStyles(() => ({
  paper: {
    borderRadius: 12,
    maxWidth: 400,
  },
  title: {
    padding: "16px 20px 8px",
    fontSize: 16,
    fontWeight: 600,
  },
  content: {
    padding: "0 20px 8px",
  },
  body: {
    fontSize: 14,
    lineHeight: 1.5,
    color: "rgba(0, 0, 0, 0.72)",
  },
  actions: {
    padding: "8px 12px 12px",
    gap: 4,
  },
  reject: {
    textTransform: "none",
    fontWeight: 500,
  },
  accept: {
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
  },
}));

const IncomingRequestDialog = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { roomClient } = useContext(RoomContext);
  const meId = useSelector((state) => state.me.id);
  const session = useSelector((state) => state.remoteControl);
  const controller = useSelector((state) =>
    session && session.controllerPeerId
      ? state.peers[session.controllerPeerId]
      : null
  );
  const [busy, setBusy] = useState(false);

  const sessionOpen =
    Boolean(session) &&
    session.status === "requested" &&
    session.hostPeerId === meId &&
    Boolean(session.sessionId);

  // Keep dialog mounted while accept/reject finishes so MUI backdrop unmounts cleanly.
  const dialogOpen = sessionOpen || busy;

  useEffect(() => {
    if (!sessionOpen) setBusy(false);
  }, [sessionOpen]);

  const displayName =
    (controller && controller.displayName) ||
    intl.formatMessage({
      id: "label.participant",
      defaultMessage: "Participant",
    });

  return (
    <Dialog
      open={dialogOpen}
      disableBackdropClick={sessionOpen}
      disableEscapeKeyDown={sessionOpen}
      keepMounted={false}
      classes={{ paper: classes.paper }}
    >
      <DialogTitle className={classes.title} disableTypography>
        <FormattedMessage
          id="remoteControl.incomingTitle"
          defaultMessage="Remote control request"
        />
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Typography className={classes.body}>
          <FormattedMessage
            id="remoteControl.incomingBody"
            defaultMessage="{displayName} wants to control your shared screen."
            values={{ displayName }}
          />
        </Typography>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          className={classes.reject}
          disabled={busy || !sessionOpen}
          onClick={async () => {
            setBusy(true);
            try {
              await roomClient.rejectRemoteControl();
            } finally {
              setBusy(false);
            }
          }}
        >
          <FormattedMessage id="label.reject" defaultMessage="Decline" />
        </Button>
        <Button
          className={classes.accept}
          color="primary"
          variant="contained"
          disabled={busy || !sessionOpen}
          onClick={async () => {
            setBusy(true);
            try {
              await roomClient.acceptRemoteControl();
            } finally {
              setBusy(false);
            }
          }}
        >
          <FormattedMessage id="label.accept" defaultMessage="Accept" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncomingRequestDialog;
